import PublicMenuItem from './PublicMenuItem.js'
import RestaurantCustomSiteField from './RestaurantCustomSiteField.js'

/**
 * Public restaurant payload from GET /api/public/restaurants/{slug}.
 * Use PublicRestaurant.fromApi(apiResponse) when consuming the public endpoint.
 * No internal id; payload matches docs/API-REFERENCE.md (name, slug, operating_hours, menu_items, etc.).
 */

export default class PublicRestaurant {
  constructor(data = {}) {
    this._uuid = data.uuid ?? null
    this._name = data.name ?? ''
    this._tagline = data.tagline ?? null
    this._primaryColor = data.primary_color ?? data.primaryColor ?? null
    this._slug = data.slug ?? ''
    this._businessType = data.business_type ?? data.businessType ?? 'restaurants'
    this._logoUrl = data.logo_url ?? data.logoUrl ?? null
    this._bannerUrl = data.banner_url ?? data.bannerUrl ?? null
    this._defaultLocale = data.default_locale ?? data.defaultLocale ?? 'en'
    this._currency = data.currency ?? 'USD'
    this._showCurrency = data.show_currency !== false
    this._address = data.address ?? null
    this._latitude = data.latitude ?? null
    this._longitude = data.longitude ?? null
    this._googleMapsUrl = data.google_maps_url ?? data.googleMapsUrl ?? null
    /** Legacy profile fields (also exposed on owner API); merged into contact UI with `contacts`. */
    this._phone = data.phone ?? ''
    this._email = data.email ?? ''
    this._website = data.website ?? ''
    const rawSocial = data.social_links ?? data.socialLinks ?? {}
    this._socialLinks =
      rawSocial && typeof rawSocial === 'object' && !Array.isArray(rawSocial) ? { ...rawSocial } : {}
    this._operatingHours = data.operating_hours ?? data.operatingHours ?? null
    this._languages = Array.isArray(data.languages) ? [...data.languages] : []
    this._locale = data.locale ?? this._defaultLocale
    this._description = data.description ?? null
    this._menuItems = Array.isArray(data.menu_items) ? data.menu_items.map((i) => PublicMenuItem.fromApi(i)) : []
    this._menuGroups = Array.isArray(data.menu_groups)
      ? data.menu_groups.map((g) => ({
          category_name: g.category_name ?? 'Menu',
          category_uuid: g.category_uuid ?? null,
          category_description: g.category_description ?? null,
          availability: g.availability ?? null,
          image_url: g.image_url ?? null,
          items: Array.isArray(g.items) ? g.items.map((i) => PublicMenuItem.fromApi(i)) : [],
        }))
      : []
    this._feedbacks = Array.isArray(data.feedbacks) ? data.feedbacks.map((f) => ({ ...f })) : []
    this._contacts = Array.isArray(data.contacts)
      ? data.contacts.map((c) => ({
          uuid: c.uuid ?? null,
          type: c.type ?? 'phone',
          value: c.value ?? c.number ?? '',
          number: c.number ?? '',
          label: c.label ?? null,
        }))
      : []
    const viewer = (data.viewer && typeof data.viewer === 'object') ? data.viewer : {}
    this._viewer = {
      is_owner: viewer.is_owner === true,
      owner_admin_url: typeof viewer.owner_admin_url === 'string' && viewer.owner_admin_url.trim() !== ''
        ? viewer.owner_admin_url
        : null,
      needs_data: viewer.needs_data === true,
    }
    this._template = data.template ?? 'template-1'
    this._yearEstablished = data.year_established ?? data.yearEstablished ?? null
    const ann = data.active_announcement ?? data.activeAnnouncement ?? null
    this._activeAnnouncement =
      ann && typeof ann === 'object' && (ann.message != null || ann.show_until != null)
        ? {
            message: ann.message ?? null,
            image_url: ann.image_url ?? null,
            show_until: ann.show_until ?? null,
          }
        : null
    this._mailingListAvailable = data.mailing_list_available === true
    this._feedbackAvailable = data.feedback_available === true
    this._reservationAvailable = data.reservation_available === true
    this._hideFooterCredits = data.hide_footer_credits === true
    this._quickOrderAvailable = data.quick_order_available === true
    this._quickOrderPhone = data.quick_order_phone ?? null
    /** Pro/VIP: public cart uses API checkout only (no WhatsApp/SMS / pickup-dine picker). */
    this._quickOrderCheckoutOnly = data.quick_order_checkout_only === true
    this._customSiteFields = Array.isArray(data.custom_site_fields)
      ? data.custom_site_fields.map((row) => RestaurantCustomSiteField.fromApi(row).toJSON())
      : []
    this._galleryItems = Array.isArray(data.gallery_items)
      ? data.gallery_items.map((row) => ({
          uuid: row.uuid ?? null,
          sort_order: Number(row.sort_order ?? 0),
          titles: row.titles && typeof row.titles === 'object' ? { ...row.titles } : {},
          descriptions:
            row.descriptions && typeof row.descriptions === 'object' ? { ...row.descriptions } : {},
          image_url: row.image_url ?? null,
        }))
      : []
  }

  get uuid() {
    return this._uuid
  }

  get name() {
    return this._name
  }

  get tagline() {
    return this._tagline
  }

  get primary_color() {
    return this._primaryColor
  }

  get slug() {
    return this._slug
  }

  /** restaurants | ecommerce | services; used for nav/section labels (Menu | Products | Services). */
  get business_type() {
    return this._businessType
  }

  get logo_url() {
    return this._logoUrl
  }

  get banner_url() {
    return this._bannerUrl
  }

  get default_locale() {
    return this._defaultLocale
  }

  get currency() {
    return this._currency
  }

  /** When true (default), show currency symbol/code with prices; when false, show number only. */
  get show_currency() {
    return this._showCurrency
  }

  get address() {
    return this._address
  }

  get latitude() {
    return this._latitude
  }

  get longitude() {
    return this._longitude
  }

  get google_maps_url() {
    return this._googleMapsUrl
  }

  /** Legacy main phone (may duplicate an entry in `contacts`). */
  get phone() {
    return this._phone
  }

  /** Legacy business email (Contact & links has no email type). */
  get email() {
    return this._email
  }

  /** Legacy website URL. */
  get website() {
    return this._website
  }

  /** Legacy social URLs object (facebook, instagram, twitter). */
  get social_links() {
    return { ...this._socialLinks }
  }

  get operating_hours() {
    return this._operatingHours
  }

  get languages() {
    return this._languages
  }

  get locale() {
    return this._locale
  }

  get description() {
    return this._description
  }

  get menu_items() {
    return this._menuItems
  }

  /** Menu groups with PublicMenuItem instances in items. */
  get menu_groups() {
    return this._menuGroups
  }

  get feedbacks() {
    return this._feedbacks
  }

  /** Active contacts only (public payload: uuid, type, number, label). */
  get contacts() {
    return this._contacts
  }

  get viewer() {
    return this._viewer
  }

  get template() {
    return this._template
  }

  get year_established() {
    return this._yearEstablished
  }

  /** When present, show announcement banner on public site (message, show_until). Paid feature; visible for 4 days. */
  get active_announcement() {
    return this._activeAnnouncement
  }

  /** True when owner is paid; show mailing list signup section on public templates. */
  get mailing_list_available() {
    return this._mailingListAvailable
  }

  /** True when owner has Growth/Pro (feedback feature); show reviews and feedback form on public templates. */
  get feedback_available() {
    return this._feedbackAvailable
  }

  /** True when owner has table_reservation (Pro); show reservation form on public templates. */
  get reservation_available() {
    return this._reservationAvailable
  }

  /** When true, frontend hides the credits block ("Site created via QRestoo" / DLR). Paid users can set in settings. */
  get hide_footer_credits() {
    return this._hideFooterCredits
  }

  /** When true, visitors can add items to a cart and submit quick orders. */
  get quick_order_available() {
    return this._quickOrderAvailable
  }

  /** Phone number for WhatsApp/Viber/SMS quick order delivery; null when only email is available. */
  get quick_order_phone() {
    return this._quickOrderPhone
  }

  /** When true, Quick Order cart shows Checkout → API instead of messaging options. */
  get quick_order_checkout_only() {
    return this._quickOrderCheckoutOnly
  }

  /** VIP-only custom fields for integrations (empty when owner is not VIP). */
  get custom_site_fields() {
    return this._customSiteFields
  }

  /** VIP-only visible gallery items (empty when owner is not VIP). */
  get gallery_items() {
    return this._galleryItems
  }

  /** Build from API response (e.g. { data } from getPublicRestaurant). */
  static fromApi(apiResponse) {
    const data = apiResponse?.data ?? apiResponse
    return new PublicRestaurant(data ?? {})
  }

  /** Plain object for template binding (same shape as API). */
  toJSON() {
    return {
      uuid: this._uuid,
      name: this._name,
      tagline: this._tagline,
      primary_color: this._primaryColor,
      slug: this._slug,
      business_type: this._businessType,
      logo_url: this._logoUrl,
      banner_url: this._bannerUrl,
      default_locale: this._defaultLocale,
      currency: this._currency,
      show_currency: this._showCurrency,
      address: this._address,
      latitude: this._latitude,
      longitude: this._longitude,
      google_maps_url: this._googleMapsUrl,
      phone: this._phone || null,
      email: this._email || null,
      website: this._website || null,
      social_links: { ...this._socialLinks },
      operating_hours: this._operatingHours,
      languages: [...this._languages],
      locale: this._locale,
      description: this._description,
      menu_items: this._menuItems.map((i) => (i.toJSON ? i.toJSON() : { ...i })),
      menu_groups: this._menuGroups.map((g) => ({
        category_name: g.category_name,
        category_uuid: g.category_uuid,
        category_description: g.category_description ?? null,
        availability: g.availability ?? null,
        image_url: g.image_url ?? null,
        items: (g.items ?? []).map((i) => (i.toJSON ? i.toJSON() : { ...i })),
      })),
      feedbacks: this._feedbacks.map((f) => ({ ...f })),
      contacts: this._contacts.map((c) => ({ ...c })),
      viewer: {
        is_owner: this._viewer.is_owner === true,
        owner_admin_url: this._viewer.owner_admin_url ?? null,
        needs_data: this._viewer.needs_data === true,
      },
      template: this._template,
      year_established: this._yearEstablished,
      active_announcement: this._activeAnnouncement,
      mailing_list_available: this._mailingListAvailable,
      feedback_available: this._feedbackAvailable,
      reservation_available: this._reservationAvailable,
      hide_footer_credits: this._hideFooterCredits,
      quick_order_available: this._quickOrderAvailable,
      quick_order_phone: this._quickOrderPhone,
      quick_order_checkout_only: this._quickOrderCheckoutOnly,
      custom_site_fields: [...this._customSiteFields],
      gallery_items: this._galleryItems.map((g) => ({ ...g })),
    }
  }
}
